import { useMemo } from 'react';
import { LocalSeoAllSx } from 'src/content/localSeo/localSeoCss';
import DropdownPopover from '../UI/DropdownPopover';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import {
  GoogleBusinessProfileActionType,
  useGoogleBusinessProfile
} from './GoogleBusinessProfileProvider';
import { SqSpIconPath, SqSpIconTooltipLocalSEOText } from 'src/common/config/TextMessage';
import { BusinessProfile } from './types';

import { useDispatch } from 'react-redux';
import {
  setSelectedBusinessName,
  setSelectedLocationId,
  setSelectedAccountId
} from 'src/redux/localSeo';

export const GoogleBusinessProfileSelector = () => {
  const { state, dispatch, loading } = useGoogleBusinessProfile();
  const dispatchRedux = useDispatch();
  const { selectedBusinessProfile, allBusinessProfiles } = state;

  const locationsOptions = useMemo(() => {
    return (allBusinessProfiles || []) as BusinessProfile[];
  }, [allBusinessProfiles]);

  const selectedProfile = useMemo(() => {
    if (!selectedBusinessProfile || !allBusinessProfiles?.length) {
      return null;
    }

    const selectedName =
      typeof selectedBusinessProfile === 'object'
        ? selectedBusinessProfile?.name
        : undefined;

    if (!selectedName) {
      return null;
    }

    return (
      (allBusinessProfiles as BusinessProfile[]).find(
        (profile) => profile?.name === selectedName
      ) || null
    );
  }, [selectedBusinessProfile, allBusinessProfiles]);

  if (locationsOptions?.length === 0) {
    return null;
  }

  return (
    <DropdownPopover
      startIcon={'/static/images/icons/local-seo/citation-dropdown-email.svg'}
      selectedValue={selectedProfile}
      options={locationsOptions}
      onSelect={(value: BusinessProfile) => {
        // Update Context
        dispatch({
          type: GoogleBusinessProfileActionType.SET_GOOGLE_BUSINESS_PROFILE,
          payload: {
            selectedBusinessProfile: {
              accountId: value?.accountId,
              name: value?.name,
              locationId: value?.locationId
            }
          }
        });

        // Sync with Redux for components that rely on it (like YourPerformanceCard)
        dispatchRedux(setSelectedBusinessName(value?.title));
        dispatchRedux(setSelectedLocationId(value?.name));
        dispatchRedux(setSelectedAccountId(value?.accountId));
      }}
      loading={loading}
      isArrayOfObject={true}
      labelKey="title"
      idKey="name"
      renderOption={(option: BusinessProfile, isSelected: boolean) => {
        return (
          <Typography
            component="div"
            sx={
              isSelected
                ? LocalSeoAllSx?.selectedBusinessNameText
                : LocalSeoAllSx?.nonSelectedBusinessNameText
            }
          >
            <Box display="flex" alignItems="center" gap={1}>
              {option?.title}
              {option?.isConnectedWithSqsp && (
                <Tooltip
                  title={
                    <div className="flex items-center">
                      <p
                        style={{
                          color: 'white',
                          fontSize: '12px'
                        }}
                      >
                        {SqSpIconTooltipLocalSEOText}
                      </p>
                    </div>
                  }
                  placement="top"
                >
                  <Avatar
                    sx={{
                      height: '20px',
                      width: '20px',
                      borderRadius: 0
                    }}
                    src={SqSpIconPath}
                  />
                </Tooltip>
              )}
            </Box>
          </Typography>
        );
      }}
    />
  );
};
